/**
 * W7 plan-timeline model — the pure logic behind the multi-track Gantt editor.
 * No React, no DOM: date↔pixel scaling, track grouping, node CRUD, look-ahead
 * band, and the layout the hand-SVG canvas draws. The screen owns the editable
 * node list; every derive here is a pure function of it so a re-sequence or a
 * bar edit re-derives the whole canvas.
 *
 * ★ Every API-data iteration is guarded by `asArray<T>` at the boundary — a real
 * backend can return a present-but-non-array `nodes` / `tracks` (`{}`, `null`,
 * an error envelope) that `?? []` and truthy-`if` both admit, and the next
 * `.map`/`.reduce` would white-screen the app (the Oxbo-Z prod crash class).
 */
import type {
  PlanTimelineOut,
  TimelineNode,
  TimelineTrack,
  TimelineTrackKind,
  TimelineNodeKind,
} from "@api/types";
import { asArray } from "@lib/asArray";

const MS_PER_DAY = 86_400_000;

/** Zoom granularity — sets the pixels-per-day scale + the axis tick step. */
export type Zoom = "day" | "week" | "month";
/** Plan-vs-actual visibility filter. */
export type PaFilter = "both" | "plan" | "actual";

const PX_PER_DAY: Record<Zoom, number> = { day: 26, week: 11, month: 4 };
/** Axis tick spacing, in days, per zoom. */
const TICK_DAYS: Record<Zoom, number> = { day: 1, week: 7, month: 30 };

/** Parse an ISO `YYYY-MM-DD` to a UTC day-count; null/garbage → null. */
export function parseISODay(iso: string | null | undefined): number | null {
  if (!iso || typeof iso !== "string") return null;
  const ms = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.round(ms / MS_PER_DAY) : null;
}

/** Render a UTC day-count back to ISO `YYYY-MM-DD`. */
export function dayToISO(day: number): string {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}

/** Whole-day span between two ISO dates (end − start); null if either is bad. */
export function daysBetween(startISO: string, endISO: string | null): number | null {
  const s = parseISODay(startISO);
  const e = parseISODay(endISO);
  if (s == null || e == null) return null;
  return e - s;
}

/**
 * The x-axis scale: maps an ISO date to a pixel x, given a window origin and a
 * pixels-per-day derived from zoom. `originDay` is the left edge of the canvas.
 */
export interface Scale {
  originDay: number;
  pxPerDay: number;
  /** Total canvas width in px (window span × pxPerDay + gutter). */
  width: number;
  spanDays: number;
}

/** Left gutter reserved on the SVG before day 0 (keeps bars off the y-axis). */
export const GUTTER = 8;

/** Build the x-scale from the window bounds + zoom. Falls back to a 1-day span. */
export function makeScale(
  horizonStartISO: string,
  horizonEndISO: string,
  zoom: Zoom,
): Scale {
  const start = parseISODay(horizonStartISO);
  const end = parseISODay(horizonEndISO);
  const originDay = start ?? 0;
  const spanDays = start != null && end != null && end > start ? end - start : 1;
  const pxPerDay = PX_PER_DAY[zoom];
  return { originDay, pxPerDay, spanDays, width: GUTTER + spanDays * pxPerDay };
}

/** Pixel x for an ISO date under a scale (clamped to ≥ gutter). */
export function xOf(iso: string, scale: Scale): number {
  const day = parseISODay(iso);
  if (day == null) return GUTTER;
  return GUTTER + (day - scale.originDay) * scale.pxPerDay;
}

/** Pixel width for a [start,end] span under a scale (min 4px so bars show). */
export function widthOf(startISO: string, endISO: string | null, scale: Scale): number {
  if (!endISO) return 0;
  const d = daysBetween(startISO, endISO);
  if (d == null || d <= 0) return 4;
  return Math.max(4, d * scale.pxPerDay);
}

/** Axis tick marks (ISO date + x) across the window for the current zoom. */
export function axisTicks(scale: Scale, zoom: Zoom): { iso: string; x: number }[] {
  const step = TICK_DAYS[zoom];
  const ticks: { iso: string; x: number }[] = [];
  for (let d = 0; d <= scale.spanDays; d += step) {
    const iso = dayToISO(scale.originDay + d);
    ticks.push({ iso, x: GUTTER + d * scale.pxPerDay });
  }
  return ticks;
}

/** x of the Today line under a scale. */
export function todayX(todayISO: string, scale: Scale): number {
  return xOf(todayISO, scale);
}

/**
 * The look-ahead band: from Today to `lookahead_weeks` later (clamped to the
 * window end). Returns pixel [x, width]; a zero/absent horizon → zero-width.
 */
export function lookaheadBand(
  todayISO: string,
  lookaheadWeeks: number,
  scale: Scale,
): { x: number; width: number } {
  const today = parseISODay(todayISO);
  if (today == null) return { x: GUTTER, width: 0 };
  const weeks = Number.isFinite(lookaheadWeeks) && lookaheadWeeks > 0 ? lookaheadWeeks : 0;
  const endDay = Math.min(today + weeks * 7, scale.originDay + scale.spanDays);
  const x = GUTTER + (today - scale.originDay) * scale.pxPerDay;
  const width = Math.max(0, (endDay - today) * scale.pxPerDay);
  return { x, width };
}

// ───────────────────────────── node helpers ─────────────────────────────────

/** The `--op-*` CSS var for a node's operation (falls back to the brand). */
export function opVar(op: string | null): string {
  const known = new Set([
    "pad",
    "drilling",
    "completion",
    "unconv",
    "wireline",
    "ct",
    "workover",
    "pulling",
  ]);
  return op && known.has(op) ? `--op-${op}` : "--brand";
}

/** True if a node is an editable plan bar (vs a read-only actual/npt overlay). */
export function isPlanNode(n: TimelineNode): boolean {
  return n.kind === "plan";
}

/** Coerce + normalise a server node so a partial body never breaks a derive. */
function normNode(n: unknown, i: number): TimelineNode | null {
  if (!n || typeof n !== "object") return null;
  const r = n as Record<string, unknown>;
  const start = typeof r.start === "string" ? r.start : null;
  if (!start) return null; // a node with no start can't be placed
  return {
    id: typeof r.id === "string" ? r.id : `node-${i}`,
    track_id: typeof r.track_id === "string" ? r.track_id : "unassigned",
    track_kind: (r.track_kind as TimelineTrackKind) ?? "well",
    kind: (r.kind as TimelineNodeKind) ?? "plan",
    op: typeof r.op === "string" ? r.op : null,
    label: typeof r.label === "string" ? r.label : "",
    start,
    end: typeof r.end === "string" ? r.end : null,
    cpe_ref: typeof r.cpe_ref === "string" ? r.cpe_ref : null,
    seq: typeof r.seq === "number" ? r.seq : i + 1,
    depth_from: typeof r.depth_from === "number" ? r.depth_from : null,
    depth_until: typeof r.depth_until === "number" ? r.depth_until : null,
    phase: typeof r.phase === "string" ? r.phase : null,
  };
}

/** Coerce the server tracks to a clean array (guards non-array `tracks`). */
export function readTracks(data: PlanTimelineOut | undefined): TimelineTrack[] {
  return asArray<TimelineTrack>(data?.tracks)
    .filter((t): t is TimelineTrack => !!t && typeof (t as TimelineTrack).id === "string")
    .map((t) => ({
      id: t.id,
      kind: (t.kind as TimelineTrackKind) ?? "well",
      label: typeof t.label === "string" ? t.label : t.id,
      sub: typeof t.sub === "string" ? t.sub : null,
    }));
}

/**
 * Coerce the server node stream to a clean array, dropping unplaceable rows.
 * `asArray` guards the non-array-body crash class; `normNode` guards a
 * present-but-partial node. Ordered by (track, seq) so the grid + canvas agree.
 */
export function readNodes(data: PlanTimelineOut | undefined): TimelineNode[] {
  return asArray<unknown>(data?.nodes)
    .map((n, i) => normNode(n, i))
    .filter((n): n is TimelineNode => n !== null)
    .sort((a, b) => (a.track_id === b.track_id ? a.seq - b.seq : a.track_id < b.track_id ? -1 : 1));
}

// ── editable node CRUD (the screen holds the list; these return a next list) ──

/** Update one node by id with a partial patch. */
export function updateNode(
  nodes: TimelineNode[],
  id: string,
  patch: Partial<TimelineNode>,
): TimelineNode[] {
  return nodes.map((n) => (n.id === id ? { ...n, ...patch } : n));
}

/** Delete a node by id. */
export function deleteNode(nodes: TimelineNode[], id: string): TimelineNode[] {
  return nodes.filter((n) => n.id !== id);
}

/** Reorder a plan node within its track (dir −1 up / +1 down); re-seqs the track. */
export function reorderInTrack(
  nodes: TimelineNode[],
  id: string,
  dir: -1 | 1,
): TimelineNode[] {
  const node = nodes.find((n) => n.id === id);
  if (!node) return nodes;
  const band = nodes
    .filter((n) => n.track_id === node.track_id)
    .sort((a, b) => a.seq - b.seq);
  const idx = band.findIndex((n) => n.id === id);
  const target = idx + dir;
  if (idx < 0 || target < 0 || target >= band.length) return nodes;
  const swap = band[target];
  if (!swap) return nodes;
  // swap seq values, keep everyone else intact
  return nodes.map((n) => {
    if (n.id === node.id) return { ...n, seq: swap.seq };
    if (n.id === swap.id) return { ...n, seq: node.seq };
    return n;
  });
}

/** Shift a plan bar's start (and its end by the same delta) by whole days. */
export function shiftNodeDays(node: TimelineNode, deltaDays: number): TimelineNode {
  const s = parseISODay(node.start);
  if (s == null) return node;
  const nextStart = dayToISO(s + deltaDays);
  if (!node.end) return { ...node, start: nextStart };
  const e = parseISODay(node.end);
  const nextEnd = e == null ? node.end : dayToISO(e + deltaDays);
  return { ...node, start: nextStart, end: nextEnd };
}

/** Next seq for a new plan step appended to a track. */
export function nextSeqForTrack(nodes: TimelineNode[], trackId: string): number {
  const band = nodes.filter((n) => n.track_id === trackId);
  return band.reduce((m, n) => Math.max(m, n.seq), 0) + 1;
}

/** Build a fresh plan bar for a track, defaulting to a 7-day span at Today. */
export function makePlanNode(
  track: TimelineTrack,
  todayISO: string,
  nodes: TimelineNode[],
): TimelineNode {
  const today = parseISODay(todayISO) ?? 0;
  return {
    id: `plan-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    track_id: track.id,
    track_kind: track.kind,
    kind: "plan",
    op: track.kind === "cpe" ? "drilling" : null,
    label: "",
    start: dayToISO(today),
    end: dayToISO(today + 7),
    cpe_ref: null,
    seq: nextSeqForTrack(nodes, track.id),
    depth_from: null,
    depth_until: null,
    phase: null,
  };
}

// ───────────────────────── layout for the canvas ────────────────────────────

/** Fixed pixel heights per track kind (the row band the bars sit in). */
export const TRACK_HEIGHT: Record<TimelineTrackKind, number> = {
  cpe: 40,
  well: 44,
  service: 30,
  pad: 30,
};

/** A track laid out with its y-offset + height (top-down in `tracks` order). */
export interface LaidTrack extends TimelineTrack {
  y: number;
  height: number;
}

/** Stack the tracks vertically, top-down, returning y + height per track. */
export function layoutTracks(tracks: TimelineTrack[], top = 8): LaidTrack[] {
  let y = top;
  return tracks.map((t) => {
    const height = TRACK_HEIGHT[t.kind] ?? 40;
    const laid: LaidTrack = { ...t, y, height };
    y += height;
    return laid;
  });
}

/** Total canvas height for a set of laid tracks (+ axis gutter at the bottom). */
export function canvasHeight(laid: LaidTrack[], axisGutter = 28, top = 8): number {
  const last = laid[laid.length - 1];
  const bottom = last ? last.y + last.height : top;
  return bottom + axisGutter;
}

/** A node placed for drawing: pixel box + its track's y-band. */
export interface PlacedNode {
  node: TimelineNode;
  x: number;
  width: number;
  y: number;
  height: number;
}

/**
 * Place nodes into pixel boxes for the canvas, honouring the plan/actual filter
 * and the op-filter set. Milestones become zero-width markers; nodes whose track
 * is unknown are dropped (no orphan bars).
 */
export function placeNodes(
  nodes: TimelineNode[],
  laid: LaidTrack[],
  scale: Scale,
  opts: { pa: PaFilter; ops: Set<string> },
): PlacedNode[] {
  const byId = new Map(laid.map((t) => [t.id, t]));
  const placed: PlacedNode[] = [];
  for (const node of nodes) {
    const track = byId.get(node.track_id);
    if (!track) continue;
    if (!passesPaFilter(node.kind, opts.pa)) continue;
    if (node.op && !opts.ops.has(node.op)) continue;
    const x = xOf(node.start, scale);
    const width = node.kind === "milestone" ? 0 : widthOf(node.start, node.end, scale);
    // bar sits inset within its track band
    const inset = 4;
    placed.push({
      node,
      x,
      width,
      y: track.y + inset,
      height: track.height - inset * 2,
    });
  }
  return placed;
}

/** Whether a node kind is visible under the plan/actual filter. */
export function passesPaFilter(kind: TimelineNodeKind, pa: PaFilter): boolean {
  if (pa === "both") return true;
  if (pa === "plan") return kind === "plan" || kind === "milestone" || kind === "transfer";
  // actual-only
  return kind === "actual" || kind === "npt" || kind === "milestone";
}

/** The distinct operation classes present in a node stream (for the op-filter). */
export function opsPresent(nodes: TimelineNode[]): string[] {
  const set = new Set<string>();
  for (const n of nodes) if (n.op) set.add(n.op);
  return [...set];
}

/** Count of authored plan bars (the KPI + the empty-state test hinge). */
export function planNodeCount(nodes: TimelineNode[]): number {
  return nodes.filter(isPlanNode).length;
}
